import { remove, runInAction, set } from 'mobx';

import type {
  CustomFields as CustomFieldsCollection,
  IImportColumn,
  Importer,
  TImporterState,
} from '@feathr/blackbox';
import { FieldCollection } from '@feathr/blackbox';

/*
 * A failed import is not a dead end: the usual recovery is to re-upload a
 * corrected file and run it again, so the wizard stays editable and the
 * Import/Save actions stay enabled in that state as well as in 'draft'.
 * 'pending' and 'processing' are in flight and 'complete' is done, so all three stay locked.
 */
const EDITABLE_STATES = new Set<TImporterState>(['draft', 'failed']);

export function isImporterEditable(state?: TImporterState): boolean {
  return !!state && EDITABLE_STATES.has(state);
}

/**
 * Marks each column mapping whose custom field hasn't been created yet, and unmarks the rest.
 *
 * @param importer - The import whose column mappings to mark
 * @param customFields - The collection holding the mapped fields
 */
export function markNewCustomFields(
  importer: Importer,
  customFields: CustomFieldsCollection,
): void {
  const newFieldIds = new Set(
    importer
      .getUsedCustomFields(customFields)
      // A placeholder is an existing field that is loading or failed to load, such as a deleted one.
      .filter((field) => field.isEphemeral && !field.isPlaceholder)
      .map((field) => field.id),
  );
  const isNew = (mapper: IImportColumn): boolean =>
    !!mapper.feathr_attr && newFieldIds.has(mapper.feathr_attr);
  const mappers: IImportColumn[] = importer.get('column_mappers');
  const changed = mappers.filter((mapper) => (isNew(mapper) ? !mapper.is_new : 'is_new' in mapper));
  if (!changed.length) {
    return;
  }
  runInAction(() => {
    changed.forEach((mapper) => {
      if (isNew(mapper)) {
        set(mapper, { is_new: true });
      } else {
        remove(mapper, 'is_new');
      }
    });
  });
  importer.setAttributeDirty('column_mappers');
}

/**
 * Rebuilds each custom field a draft marked as new, as an unsaved field, from its column mapping.
 *
 * @param importer - The import whose column mappings name the fields
 * @param customFields - The collection to rebuild the fields in
 */
export function restoreNewCustomFields(
  importer: Importer,
  customFields: CustomFieldsCollection,
): void {
  if (!isImporterEditable(importer.get('state'))) {
    return;
  }
  const mappers: IImportColumn[] = importer.get('column_mappers');
  mappers.forEach((mapper) => {
    if (!mapper.is_new || !mapper.feathr_attr) {
      return;
    }
    customFields.create({
      id: mapper.feathr_attr,
      collection: FieldCollection.Person,
      data_type: mapper.attr_type,
      u_key: mapper.u_key ?? '',
    });
  });
}

/*
 * The campaign "Import contacts" buttons add this to the wizard URL. That import feeds a
 * campaign group, so it is always a contact import and step 1 offers no type choice.
 */
const CONTACTS_ONLY_PARAM = 'type';
const CONTACTS_ONLY_VALUE = 'contacts';

export function getContactsOnlyImportUrl(url: string): string {
  return `${url}?${new URLSearchParams({ [CONTACTS_ONLY_PARAM]: CONTACTS_ONLY_VALUE })}`;
}

export function isContactsOnlyImport(search: string): boolean {
  return new URLSearchParams(search).get(CONTACTS_ONLY_PARAM) === CONTACTS_ONLY_VALUE;
}
